import * as React from 'react'
import { ChevronDown, ChevronRight, Plus } from 'lucide-react'
import { useProductSearch } from '@/hooks/useProducts'
import { Input } from '@/components/ui/input'
import type { components } from '@/lib/api/generated'
import type { POLineItemFormValues } from '@/lib/schemas/po'
import { signalKind } from '@/hooks/useDashboard'

type ProductRow = components['schemas']['DashboardRow']

// Shared mapper: a dashboard product row → a PO line-item form value.
// `qty` lets the scan path seed qty 1 while a manual click keeps using the
// dashboard's recommended_qty (falling back to 1).
export function productToLineItem(
  p: ProductRow,
  qty?: number
): POLineItemFormValues {
  return {
    variant_id: Number(p.variant_id ?? 0),
    product_id: Number(p.product_id ?? 0),
    product_title: p.product_title ?? '',
    variant_title: p.variant_title ?? '',
    sku: p.sku ?? '',
    barcode: p.barcode ?? '',
    vendor: p.vendor ?? '',
    retail_price: 0,
    cost_base: p.unit_cost ?? 0,
    qty_ordered: qty ?? (p.recommended_qty || 1),
    notes: '',
    on_hand: p.on_hand ?? 0,
    incoming: p.incoming ?? 0,
    rec_qty: p.recommended_qty ?? undefined,
    signal: signalKind(p.action_signal),
    return_rate: p.return_rate ?? undefined,
  }
}

function fmtNum(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '—'
  return Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 })
}

function fmtCurrency(n: number | null | undefined): string {
  if (n == null) return '—'
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n)
}

interface ProductGroup {
  productId: string
  title: string
  vendor: string
  variants: ProductRow[]
}

interface Props {
  onPick: (item: POLineItemFormValues) => void
  // Called when the input receives a barcode typed at scanner speed and
  // committed with Enter. The StepItems owner resolves the barcode and
  // adds/increments the matching line item.
  onScan?: (code: string) => void
  disabled?: boolean
  // Session 7.5 — scope results to the PO's recipient:
  //   supplier set  → backend resolves to all vendors that supplier carries
  //   vendor set    → results limited to that one vendor
  //   both null     → unscoped search (legacy behavior)
  supplier?: string | null
  vendor?: string | null
  // Variant IDs already on the PO — rows for these show an "Added" badge
  // instead of an Add button, and re-adding is a no-op (no duplicate lines).
  // Passed in (rather than read from form context) so this component also works
  // outside a form, e.g. the Receiving page. Defaults to none.
  addedVariantIds?: Set<number>
  // Receiving flow: when a search resolves to exactly one product whose barcode
  // equals the query (i.e. the user scanned a barcode), add it automatically
  // without a manual click and keep focus in the box for the next scan. Also
  // auto-focuses the input on mount. Off by default so the PO wizard keeps its
  // grouped browse-and-expand behavior.
  autoPickOnBarcodeMatch?: boolean
}

// A hardware barcode scanner emits its characters far faster than a human
// types. We treat the Enter that terminates a fast burst as a scan and route
// it to onScan instead of leaving it for a manual click.
const SCAN_CHAR_GAP_MS = 40
const SCAN_MIN_FAST_CHARS = 3

// Cap the number of product titles rendered at once so a huge in-scope catalog
// doesn't paint thousands of DOM rows. The user types to narrow past this.
const MAX_GROUPS = 100

const EMPTY_VARIANT_IDS: Set<number> = new Set()

export function ProductSearchPicker({
  onPick,
  onScan,
  disabled,
  supplier,
  vendor,
  addedVariantIds = EMPTY_VARIANT_IDS,
  autoPickOnBarcodeMatch = false,
}: Props) {
  const [query, setQuery] = React.useState('')
  const [debounced, setDebounced] = React.useState('')
  const [open, setOpen] = React.useState(false)
  const [expandedId, setExpandedId] = React.useState<string | null>(null)
  const containerRef = React.useRef<HTMLDivElement>(null)
  // Tracks how many of the current input's characters arrived at scanner
  // speed. Reset on Enter (commit) and on any slow keystroke.
  const burstRef = React.useRef({ fast: 0, last: 0 })
  const inputRef = React.useRef<HTMLInputElement>(null)
  // Last barcode auto-added, so a single resolved search can't double-fire
  // while its results are still on screen.
  const lastAutoPicked = React.useRef<string | null>(null)

  React.useEffect(() => {
    const t = setTimeout(() => setDebounced(query), 250)
    return () => clearTimeout(t)
  }, [query])

  // Close the dropdown on an outside click (a click inside — on a title or
  // variant row — keeps it open so the user can add several items in a row).
  React.useEffect(() => {
    if (!open) return
    function onDocMouseDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onDocMouseDown)
    return () => document.removeEventListener('mousedown', onDocMouseDown)
  }, [open])

  const { data: results = [], isLoading } = useProductSearch(debounced, {
    supplier,
    vendor,
  })

  // Group variants under their product so the dropdown shows one title row per
  // product (per the picker spec: titles only, expand to reveal variants).
  // Alphabetical by title.
  const groups = React.useMemo<ProductGroup[]>(() => {
    const map = new Map<string, ProductGroup>()
    for (const p of results) {
      const pid = String(p.product_id ?? '')
      if (!pid || pid === '0') continue
      let g = map.get(pid)
      if (!g) {
        g = {
          productId: pid,
          title: p.product_title ?? '(untitled)',
          vendor: p.vendor ?? '',
          variants: [],
        }
        map.set(pid, g)
      }
      g.variants.push(p)
    }
    return Array.from(map.values()).sort((a, b) =>
      a.title.localeCompare(b.title, undefined, { sensitivity: 'base' })
    )
  }, [results])

  function handlePick(p: ProductRow) {
    const variantId = Number(p.variant_id ?? 0)
    const productId = Number(p.product_id ?? 0)
    if (!variantId || !productId) return
    onPick(productToLineItem(p))
    if (autoPickOnBarcodeMatch) {
      // Scan flow: clear the box, close the list so it doesn't cover the
      // items below, and refocus so the next scan lands here without a click.
      setQuery('')
      setDebounced('')
      setOpen(false)
      inputRef.current?.focus()
    }
    // PO wizard browse mode: keep the dropdown open + query intact so the user
    // can keep adding more items from the same list.
  }

  function handleTitleClick(g: ProductGroup) {
    if (g.variants.length === 1) {
      // Single-variant product → add it straight away (user's pick). No-op if
      // it's already on the PO (the row shows "Added").
      if (!addedVariantIds.has(Number(g.variants[0].variant_id))) {
        handlePick(g.variants[0])
      }
      return
    }
    setExpandedId((prev) => (prev === g.productId ? null : g.productId))
  }

  // Scan-to-add: when results resolve to exactly one exact-barcode match for the
  // current query, add it automatically and keep the box focused for the next
  // scan. Reuses search results, so it works even though the scan completes
  // faster than the debounce.
  React.useEffect(() => {
    if (!autoPickOnBarcodeMatch || isLoading) return
    const q = debounced.trim().toLowerCase()
    if (!q) {
      lastAutoPicked.current = null
      return
    }
    if (lastAutoPicked.current === q) return
    const exact = results.filter(
      (r) => (r.barcode ?? '').trim().toLowerCase() === q
    )
    if (exact.length === 1) {
      lastAutoPicked.current = q
      handlePick(exact[0])
    }
    // handlePick is stable enough; intentionally excluded to avoid re-runs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPickOnBarcodeMatch, isLoading, debounced, results])

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    const now =
      typeof performance !== 'undefined' ? performance.now() : Date.now()
    if (e.key === 'Escape') {
      if (open) {
        // Close just the dropdown — don't let the Dialog catch this and close
        // the whole wizard.
        e.preventDefault()
        e.stopPropagation()
        setOpen(false)
      }
      return
    }
    if (e.key === 'Enter') {
      e.preventDefault()
      const value = query.trim()
      const looksScanned =
        burstRef.current.fast >= SCAN_MIN_FAST_CHARS && value.length >= 4
      burstRef.current = { fast: 0, last: 0 }
      if (looksScanned && onScan) {
        onScan(value)
        // Clear + close so the list doesn't cover the items below, and keep
        // focus here for the next scan.
        setQuery('')
        setDebounced('')
        setOpen(false)
        inputRef.current?.focus()
      }
      return
    }
    if (e.key.length === 1) {
      const gap = now - burstRef.current.last
      burstRef.current.fast =
        burstRef.current.last !== 0 && gap < SCAN_CHAR_GAP_MS
          ? burstRef.current.fast + 1
          : 0
      burstRef.current.last = now
    }
  }

  const visibleGroups = groups.slice(0, MAX_GROUPS)
  // Only show suggestions once the user has typed 2+ characters — never on
  // focus — so an empty scan box doesn't cover the items list. `open` tracks
  // Escape / outside-click dismissal; typing re-opens it.
  const showPanel = !disabled && open && debounced.trim().length >= 2

  return (
    <div className="relative" ref={containerRef}>
      <Input
        ref={inputRef}
        autoFocus={autoPickOnBarcodeMatch}
        placeholder="Search or scan products by title, SKU, or barcode…"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value)
          if (!open) setOpen(true)
        }}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        aria-label="Product search"
      />
      {showPanel && (
        <div className="absolute z-20 mt-1 max-h-[26rem] w-full overflow-y-auto rounded-md border bg-popover text-popover-foreground shadow-md">
          {isLoading && (
            <div className="px-3 py-2 text-sm text-muted-foreground">Searching…</div>
          )}
          {!isLoading && groups.length === 0 && (
            <div className="px-3 py-2 text-sm text-muted-foreground">
              No products found.
            </div>
          )}
          {!isLoading &&
            visibleGroups.map((g) => {
              const single = g.variants.length === 1
              const expanded = expandedId === g.productId
              return (
                <div key={g.productId} className="border-b last:border-b-0">
                  <button
                    type="button"
                    onClick={() => handleTitleClick(g)}
                    className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground"
                  >
                    <span className="min-w-0 flex-1 truncate font-medium" title={g.title}>
                      {g.title}
                    </span>
                    <span className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
                      {single ? (
                        addedVariantIds.has(Number(g.variants[0].variant_id)) ? (
                          <span className="font-medium text-[var(--brand)]">Added</span>
                        ) : (
                          <span className="inline-flex items-center gap-0.5 rounded border border-[var(--line-warm)] px-1.5 py-0.5 text-foreground">
                            <Plus className="h-3 w-3" /> Add
                          </span>
                        )
                      ) : (
                        <>
                          {g.variants.length} variants
                          {expanded ? (
                            <ChevronDown className="h-3.5 w-3.5" />
                          ) : (
                            <ChevronRight className="h-3.5 w-3.5" />
                          )}
                        </>
                      )}
                    </span>
                  </button>
                  {expanded && !single && (
                    <div className="bg-[var(--surface-page)] text-xs">
                      {g.variants.map((v) => {
                        const added = addedVariantIds.has(Number(v.variant_id))
                        return (
                          <button
                            key={String(v.variant_id)}
                            type="button"
                            data-testid="picker-variant-add"
                            onClick={() => {
                              if (!added) handlePick(v)
                            }}
                            className="flex w-full items-center justify-between gap-3 border-t border-[var(--line-warm)] px-3 py-1.5 pl-6 text-left hover:bg-accent hover:text-accent-foreground"
                          >
                            <div className="min-w-0 flex-1">
                              <div className="font-medium">{v.variant_title || '—'}</div>
                              <div className="text-[var(--mute)]">SKU {v.sku || '—'}</div>
                            </div>
                            <div className="flex shrink-0 items-center gap-3 tabular-nums text-[var(--ink-2)]">
                              <span>Stock: {fmtNum(v.on_hand)}</span>
                              <span>30D: {fmtNum(v.qty_sold_30d)}</span>
                              <span>60D: {fmtNum(v.qty_sold_60d)}</span>
                              <span>{fmtCurrency(v.unit_cost)}</span>
                              {added ? (
                                <span className="font-medium text-[var(--brand)]">Added</span>
                              ) : (
                                <span className="inline-flex items-center gap-0.5 rounded border border-[var(--line-warm)] px-1.5 py-0.5 text-foreground">
                                  <Plus className="h-3 w-3" /> Add
                                </span>
                              )}
                            </div>
                          </button>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
          {!isLoading && groups.length > MAX_GROUPS && (
            <div className="px-3 py-2 text-xs text-muted-foreground">
              Showing first {MAX_GROUPS} of {groups.length} — type to narrow…
            </div>
          )}
        </div>
      )}
    </div>
  )
}
